import { IconBuildingStore, IconChevronDown } from "@tabler/icons-react";

/** Sub-filter within the "For payment" tab. Paid rows live in their own tab. */
export type StatusFilter = "all" | "pending_approval" | "approved";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All open invoices" },
  { value: "pending_approval", label: "Awaiting approval" },
  { value: "approved", label: "Approved, not paid" },
];

const selectClass =
  "w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-none transition appearance-none bg-white";

const labelClass = "block text-sm font-medium text-gray-700 mb-2";

const VendorSelect = ({
  vendor,
  vendorOptions,
  onVendorChange,
}: {
  vendor: string;
  vendorOptions: string[];
  onVendorChange: (value: string) => void;
}) => (
  <div className="flex-1">
    <label htmlFor="payable-vendor" className={labelClass}>
      Vendor
    </label>
    <div className="relative">
      <IconBuildingStore
        size={18}
        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10"
      />
      <select
        id="payable-vendor"
        value={vendor}
        onChange={(e) => onVendorChange(e.target.value)}
        className={`${selectClass} pl-10`}
      >
        <option value="">All vendors</option>
        {vendorOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <IconChevronDown
        size={18}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10"
      />
    </div>
  </div>
);

export interface ForPaymentFiltersProps {
  status: StatusFilter;
  vendor: string;
  vendorOptions: string[];
  onStatusChange: (value: StatusFilter) => void;
  onVendorChange: (value: string) => void;
}

export const ForPaymentFilters = ({
  status,
  vendor,
  vendorOptions,
  onStatusChange,
  onVendorChange,
}: ForPaymentFiltersProps) => (
  <div className="flex flex-col sm:flex-row gap-4 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
    <div className="flex-1">
      <label htmlFor="payable-status" className={labelClass}>
        Status
      </label>
      <div className="relative">
        <select
          id="payable-status"
          value={status}
          onChange={(e) => onStatusChange(e.target.value as StatusFilter)}
          className={selectClass}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <IconChevronDown
          size={18}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
        />
      </div>
    </div>

    <VendorSelect
      vendor={vendor}
      vendorOptions={vendorOptions}
      onVendorChange={onVendorChange}
    />
  </div>
);

export interface PaidFiltersProps {
  from: string;
  to: string;
  vendor: string;
  vendorOptions: string[];
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onVendorChange: (value: string) => void;
  onClear: () => void;
}

export const PaidFilters = ({
  from,
  to,
  vendor,
  vendorOptions,
  onFromChange,
  onToChange,
  onVendorChange,
  onClear,
}: PaidFiltersProps) => (
  <div className="flex flex-col sm:flex-row gap-4 bg-white rounded-lg p-4 shadow-sm border border-gray-200">
    <div className="flex-1">
      <label htmlFor="paid-from" className={labelClass}>
        Paid from
      </label>
      <input
        id="paid-from"
        type="date"
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-none transition bg-white"
      />
    </div>

    <div className="flex-1">
      <label htmlFor="paid-to" className={labelClass}>
        Paid to
      </label>
      <input
        id="paid-to"
        type="date"
        value={to}
        onChange={(e) => onToChange(e.target.value)}
        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 outline-none transition bg-white"
      />
    </div>

    <VendorSelect
      vendor={vendor}
      vendorOptions={vendorOptions}
      onVendorChange={onVendorChange}
    />

    {(from || to || vendor) && (
      <div className="flex items-end">
        <button
          onClick={onClear}
          className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition whitespace-nowrap"
        >
          Clear
        </button>
      </div>
    )}
  </div>
);
